import { useCallback, useEffect, useRef, useState } from 'react'
import { findPushOption } from '@/lib/handoff-rule.converters'
import type {
  ActionGroup as EditorActionGroup,
  PushFieldDropdownItem,
  PushFieldDropdownItems,
  PushOption,
} from '@/components/actions/ActionEditor'

export type DropdownSourceFetchers = Record<
  string,
  () => Promise<PushFieldDropdownItem[]>
>

// Collects the set of dropdownSource keys needed by the push integration
// fields currently in use across all action groups.
export function collectDropdownSources(
  actionGroups: EditorActionGroup[],
  pushOptions: PushOption[],
): Set<string> {
  const sources = new Set<string>()
  for (const g of actionGroups) {
    for (const a of g.actions) {
      if (a.type !== 'PUSH_INTEGRATION') continue
      for (const pt of a.pushTargets ?? []) {
        const opt = findPushOption(pushOptions, pt)
        for (const f of opt?.fields ?? []) {
          if (f.inputType === 'dropdown' && f.dropdownSource) {
            sources.add(f.dropdownSource)
          }
        }
      }
    }
  }
  return sources
}

// Lazily fetches dropdown items for push integration fields (e.g. Buildium
// Task assignee, vendor, category) as they're needed.
//
// Two effects split intentionally:
//  - The "fetch new sources" effect tracks actionGroups/pushOptions so a
//    newly added push field gets its dropdown fetched, but skips sources
//    that already failed — actionGroups changes on nearly every keystroke
//    in the editor, and retrying a down integration on every keystroke
//    would hammer its API.
//  - The "retry failed sources" effect only tracks push-integration state
//    (pushOptions/integrationsLoading), so a failed source is retried when
//    the integration itself changes (reconnect, options reload), not when
//    an unrelated action field is edited.
//
// The in-flight marker's lifecycle is owned entirely by the fetch promise
// (added before the call, removed in .finally) so a source started by one
// effect run stays marked in-flight across re-renders instead of being
// cleared by unrelated effect cleanup.
//
// Cached items are scoped to the identity of `dropdownSourceFetchers`. That
// identity changes when the integration scope changes (notably a super admin
// switching company), at which point the cache is dropped and any still
// in-flight fetch from the previous scope is discarded on arrival — otherwise
// the editor would keep showing the previous company's staff/vendors/
// categories.
export function useDropdownSourceFetching(
  actionGroups: EditorActionGroup[],
  pushOptions: PushOption[],
  dropdownSourceFetchers: DropdownSourceFetchers,
  integrationsLoading: boolean,
): {
  dropdownItems: PushFieldDropdownItems
  failedSources: Set<string>
} {
  const [dropdownItems, setDropdownItems] = useState<PushFieldDropdownItems>({})
  const [failedSources, setFailedSources] = useState<Set<string>>(new Set())

  const inFlightSourcesRef = useRef(new Set<string>())
  // Identity of the fetcher set the current cache belongs to. Doubles as the
  // "is this result still relevant?" token for in-flight fetches.
  const fetchersRef = useRef(dropdownSourceFetchers)

  if (fetchersRef.current !== dropdownSourceFetchers) {
    // The integration scope changed (e.g. company switch), so everything
    // cached under the previous scope is wrong now. Resetting during render
    // rather than in an effect means React re-renders with the cleared state
    // before any effect runs, so the fetch effects below never see the stale
    // cache and immediately refetch for the new scope.
    fetchersRef.current = dropdownSourceFetchers
    // A fresh Set, not .clear() — fetches started under the previous scope
    // hold a reference to the old Set and delete from that on settle, so they
    // can't clear a marker belonging to the new scope.
    inFlightSourcesRef.current = new Set()
    setDropdownItems({})
    setFailedSources(new Set())
  }

  // Read via refs so effects can see the latest value without adding it
  // as a dependency and re-triggering on every render.
  const dropdownItemsRef = useRef(dropdownItems)
  dropdownItemsRef.current = dropdownItems
  const actionGroupsRef = useRef(actionGroups)
  actionGroupsRef.current = actionGroups
  // Read (not depended on) by both effects below, so that recording a failure
  // doesn't itself change their dependencies — the fetch-new effect shouldn't
  // rescan just because a failure was recorded, and retries should only fire
  // on integration-state change.
  const failedSourcesRef = useRef(failedSources)
  failedSourcesRef.current = failedSources

  const fetchSource = useCallback(
    (source: string) => {
      const fetchers = dropdownSourceFetchers
      const fetcher = fetchers[source]
      if (!fetcher) return

      // Captured, not read off the ref on settle: see the reset above.
      const inFlight = inFlightSourcesRef.current
      inFlight.add(source)

      // True once the integration scope has moved on from the one this fetch
      // was started for — its result belongs to a company we're no longer
      // viewing and must not be written into the cache.
      const isStale = () => fetchersRef.current !== fetchers

      fetcher()
        .then(items => {
          if (isStale()) return
          setDropdownItems(prev => ({ ...prev, [source]: items }))
          setFailedSources(prev => {
            if (!prev.has(source)) return prev
            const next = new Set(prev)
            next.delete(source)
            return next
          })
        })
        .catch(error => {
          console.error(`Failed to fetch dropdown items for ${source}:`, error)
          if (isStale()) return
          // Only create a new Set (and thus a new failedSources reference)
          // when the source wasn't already marked failed — otherwise a
          // retry that fails again would keep producing a "new" Set with
          // identical contents, re-triggering effects that depend on it.
          setFailedSources(prev =>
            prev.has(source) ? prev : new Set(prev).add(source),
          )
        })
        .finally(() => {
          inFlight.delete(source)
        })
    },
    [dropdownSourceFetchers],
  )

  useEffect(() => {
    if (integrationsLoading) return

    const sources = collectDropdownSources(actionGroups, pushOptions)

    for (const source of sources) {
      if (dropdownItemsRef.current[source]) continue
      if (failedSourcesRef.current.has(source)) continue
      if (inFlightSourcesRef.current.has(source)) continue
      if (!dropdownSourceFetchers[source]) continue

      fetchSource(source)
    }
    // failedSources is read via ref, like dropdownItems: recording a failure
    // shouldn't re-run this scan, since the only thing it would do with that
    // information is skip the source. Retries are the effect below's job.
  }, [
    actionGroups,
    pushOptions,
    dropdownSourceFetchers,
    integrationsLoading,
    fetchSource,
  ])

  useEffect(() => {
    if (integrationsLoading) return
    const currentlyFailed = failedSourcesRef.current
    if (currentlyFailed.size === 0) return

    const sources = collectDropdownSources(actionGroupsRef.current, pushOptions)

    for (const source of sources) {
      if (!currentlyFailed.has(source)) continue
      if (inFlightSourcesRef.current.has(source)) continue
      if (!dropdownSourceFetchers[source]) continue

      fetchSource(source)
    }
    // failedSources is intentionally excluded — retries are gated on
    // integration state, not on the failure itself (see failedSourcesRef).
  }, [pushOptions, dropdownSourceFetchers, integrationsLoading, fetchSource])

  return { dropdownItems, failedSources }
}
